import React from 'react';

import DataGrid from '@gogame/data-grid';
import { useChildComponent } from '@gogame/hooks-ts';
import { dateFNS } from '@gogame/intl-ts';
import {
  useScreenDispatch,
  useScreenGo,
  useScreenState,
} from '@gogame/screens';

import { CampaignActions } from 'scenes/Campaigns/GGCampaigns/components/CampaignActions';
import {
  CampaignFormEdit,
  CampaignFormDuplicateWithChaining,
} from 'scenes/Campaigns/GGCampaigns/components/CampaignForm';
import CampaignsAddPromoCodeForm from 'scenes/Campaigns/GGCampaigns/components/CampaignsAddPromoCodeForm';
import { CostModelFormEditExisting } from 'scenes/Campaigns/GGCampaigns/components/CostModelForm';
import {
  CAMPAIGN_IDS,
  CAMPAIGN_NAMES,
} from 'scenes/Campaigns/GGCampaigns/components/OwnersManagement';
import ChangeOwner from 'scenes/Campaigns/GGCampaigns/components/OwnersManagement/ChangeOwnerForm';
import TrackingLinks from 'scenes/Campaigns/GGCampaigns/components/TrackingLinks';
import {
  columnActivityTypeProps,
  columnAffiliateProps,
  columnApplicationProps,
  columnCostModelsProps,
  columnCostModelsRateProps,
  columnCountryProps,
  columnCPEEventsProps,
  columnCreatedByProps,
  columnCreatedProps,
  columnLinkExpirationDateProps,
  columnFraudProtectionProps,
  columnIDProps,
  columnLabelsProps,
  columnLastUpdatedProps,
  columnLinkTypeProps,
  columnMaxRedemptionsProps,
  columnMultipleRedemptionsProps,
  columnNameProps,
  columnNetworkProps,
  columnOwnerProps,
  columnPlatformProps,
  columnPromoCodeProps,
  columnRewardPackageProps,
  columnStatusProps,
  columnTypeProps,
  columnActionsProps,
  columnParentCampaignProps,
} from 'scenes/Campaigns/components/Columns';
import { useLoadCampaigns } from 'scenes/Campaigns/GGCampaigns/data';
import {
  SET_TABLE_SETTINGS,
  REORDER_COLUMNS,
  COLLAPSE_COLUMNS,
} from 'scenes/Campaigns/GGCampaigns/screenReducer';
import AffiliateDetails from 'scenes/Affiliates/components/AffiliateDetails';
import PromoCodeDetails from 'scenes/PromoCodes/components/PromoCodeDetails';
import SpendAdjustmentDetails from 'scenes/SpendAdjustments/components/SpendAdjustmentDetails';
import ParentCampaignDetails from 'scenes/Campaigns/ParentCampaigns/components/ParentCampaignDetails';
import { campaignDetailsLink } from 'scenes/Campaigns/GGCampaigns/navigation';
import CampaignDetailsLink from 'scenes/Campaigns/components/CampaignDetailsLink';
import CostModelsRateItem from 'scenes/Campaigns/components/CostModelsRateItem';
import PromoCode from 'scenes/Campaigns/components/PromoCode';
import { parentCampaignDetailsLink } from 'scenes/Campaigns/ParentCampaigns/navigation';

import { MoveToAnotherAffiliate } from '../CampaignForm/MoveToAnotherAffiliate';
import CampaignsAllPagesBulkActions from './CampaignsAllPagesBulkActions';
import CampaignsBulkActions from './CampaignsBulkActions';
import Toolbar from './Toolbar';

const CampaignsDataTable = () => {
  const [childComponent, openChildComponent] = useChildComponent();

  const go = useScreenGo();

  const dispatch = useScreenDispatch();
  const screenState = useScreenState();
  const { filters, tableSettings, searchQuery, searchThroughFilters, ready } =
    screenState;
  const { data, isLoading, isRefetching } = useLoadCampaigns(
    {
      filters,
      searchThroughFilters,
      searchQuery,
      tableSettings,
    },
    { enabled: ready }
  );

  const handleOpenParentCampaign = (_, { id }) => {
    openChildComponent(ParentCampaignDetails, {
      id,
    });
  };

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleEdit = ({ id, name }) => {
    openChildComponent(CampaignFormEdit, {
      name: name,
      id: id,
    });
  };
  const handleMoveToAnotherAffiliate = ({ id, name }) => {
    openChildComponent(MoveToAnotherAffiliate, {
      name: name,
      id: id,
    });
  };
  const handleDuplicate = ({ id }) => {
    openChildComponent(CampaignFormDuplicateWithChaining, {
      id: id,
      onSubmit: (campaign) => {
        if (campaign?.parentCampaign?.id) {
          go(
            `../${parentCampaignDetailsLink.absolutePath.replace(':id', campaign.parentCampaign.id)}`
          );
          return;
        }
        go(`${campaign.id}`);
      },
    });
  };

  const handleOnEditCostModelRate = ({ campaignId, id }) => {
    openChildComponent(CostModelFormEditExisting, {
      id,
      campaignId,
    });
  };

  const handleOpenSpendAdjustment = ({ id }) => {
    openChildComponent(SpendAdjustmentDetails, {
      id,
    });
  };

  const handleGenerateLinks = ({ id, application, network }) => {
    openChildComponent(TrackingLinks, {
      id,
      applicationId: application?.id,
      networkId: network?.id,
    });
  };

  const handleAddPromoCode = (selected) => {
    openChildComponent(CampaignsAddPromoCodeForm, {
      initialValues: {
        ids: selected.map(({ id }) => id),
        names: selected.map(({ name }) => name),
      },
    });
  };

  const handleOpenPromoCodeDetails = (id) => {
    openChildComponent(PromoCodeDetails, {
      id,
    });
  };

  const handleOpenAffiliate = (_, { id }) => {
    openChildComponent(AffiliateDetails, {
      id,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  const handleChangeOwner = (selectedCampaigns) => {
    const { filters, tableSettings, searchQuery } = screenState;

    const params = {
      filters,
      tableSettings,
      searchQuery,
    };

    const dates = selectedCampaigns.map(({ created }) => new Date(created));

    const min = dateFNS.max(dates);

    openChildComponent(ChangeOwner, {
      initialValues: {
        [CAMPAIGN_IDS]: selectedCampaigns.map(({ id }) => id),
        [CAMPAIGN_NAMES]: selectedCampaigns.map(({ name }) => name),
      },
      count: data?.count,
      minDate: dateFNS.subDays(min, 1),
      filter: params,
    });
  };

  const handleAllPagesChangeOwner = () => {
    const { filters, tableSettings, searchQuery } = screenState;

    const params = {
      filters,
      tableSettings,
      searchQuery,
    };

    const min = new Date();

    openChildComponent(ChangeOwner, {
      initialValues: {
        [CAMPAIGN_IDS]: [],
      },
      count: data?.count,
      minDate: dateFNS.subDays(min, 1),
      filter: params,
    });
  };

  const selectedRowsToolbarActions = ({
    selectedValues,
    reset,
    allPagesSelected,
  }) => {
    return allPagesSelected ? (
      <CampaignsAllPagesBulkActions onChangeOwner={handleAllPagesChangeOwner} />
    ) : (
      <CampaignsBulkActions
        selectedValues={selectedValues}
        reset={reset}
        campaigns={data?.rows}
        onAddPromoCode={handleAddPromoCode}
        onChangeOwner={handleChangeOwner}
      />
    );
  };

  return (
    <React.Fragment>
      {childComponent}
      <DataGrid
        selectedRowsToolbarActions={selectedRowsToolbarActions}
        checkboxSelection={true}
        allPageSelection={true}
        rows={data?.rows}
        count={data?.count}
        loading={isLoading}
        fetching={isRefetching}
        ready={ready}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        toolbar={<Toolbar />}
        storageKey={'campaigns'}
        defaultColumnWidth={120}
        filtered={!!screenState.searchQuery}
      >
        <DataGrid.Column {...columnIDProps} />
        <DataGrid.Column
          {...columnNameProps}
          linkComponent={({ children, row }) => (
            <CampaignDetailsLink row={row} detailsLink={campaignDetailsLink}>
              {children}
            </CampaignDetailsLink>
          )}
        />
        <DataGrid.Column
          {...columnParentCampaignProps}
          onLinkClick={handleOpenParentCampaign}
        />
        <DataGrid.Column {...columnStatusProps} />
        <DataGrid.Column
          {...columnAffiliateProps}
          onLinkClick={handleOpenAffiliate}
        />
        <DataGrid.Column {...columnCostModelsProps} />
        <DataGrid.Column {...columnCPEEventsProps} />
        <DataGrid.Column
          {...columnCostModelsRateProps}
          getItemValue={(v, colDef, row) => (
            <CostModelsRateItem value={v} column={colDef} row={row} />
          )}
          onEditCostModelRate={handleOnEditCostModelRate}
          onOpenSpendAdjustment={handleOpenSpendAdjustment}
        />
        <DataGrid.Column {...columnApplicationProps} />
        <DataGrid.Column {...columnNetworkProps} />
        <DataGrid.Column {...columnPlatformProps} />
        <DataGrid.Column {...columnCountryProps} />
        <DataGrid.Column
          {...columnPromoCodeProps}
          onCreate={handleAddPromoCode}
          onShowDetails={handleOpenPromoCodeDetails}
          renderCell={(p) => <PromoCode {...p} />}
        />
        <DataGrid.Column {...columnActivityTypeProps} />
        <DataGrid.Column {...columnTypeProps} />
        <DataGrid.Column {...columnFraudProtectionProps} />
        <DataGrid.Column {...columnLabelsProps} />
        <DataGrid.Column {...columnOwnerProps} />
        <DataGrid.Column {...columnLinkTypeProps} />
        <DataGrid.Column {...columnLinkExpirationDateProps} />
        <DataGrid.Column {...columnRewardPackageProps} />
        <DataGrid.Column {...columnMultipleRedemptionsProps} />
        <DataGrid.Column {...columnMaxRedemptionsProps} />
        <DataGrid.Column {...columnLastUpdatedProps} />
        <DataGrid.Column {...columnCreatedByProps} />
        <DataGrid.Column {...columnCreatedProps} />
        <DataGrid.Column
          {...columnActionsProps}
          actionsComponent={CampaignActions}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          onGenerateLinks={handleGenerateLinks}
          onMoveToAnotherAffiliate={handleMoveToAnotherAffiliate}
          limit={3}
        />
      </DataGrid>
    </React.Fragment>
  );
};

export default CampaignsDataTable;
